import { useEffect, useState, useContext } from 'react';
import { Box } from '@mui/system';
import { makeStyles } from '@mui/styles';
import { Typography, Button, FormControlLabel, Checkbox } from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
// Services
import { Users } from 'lib/api';
// Utils and Helpers
import { EULAS } from 'helpers/constants';
// Assets
import { ReactComponent as CardDouble } from 'assets/modalKYX/Card_double.svg';

const useStyles = makeStyles({
    banner: {
        backgroundColor: '#182D55 !important',
        maxWidth: '16em',
        minHeight: '100%',
        paddingTop: '3em',
        borderTopLeftRadius: '10px',
        borderBottomLeftRadius: '10px'
    },
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    bannerTitle: {
        color: 'white',
        fontWeight: '700!important'
    },
    bannerSpan: {
        color: 'white'
    },
    bannerText: {
        margin: '1em'
    },
    dialogContent: {
        backgroundColor: 'transparent!important',
        padding: '0!important',
        minHeight: '600px',
        minWidth: '64em'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontSize: '21px',
        fontWeight: '700!important'
    },
    verifyOption: {
        borderRadius: '15px',
        border: '1px solid #d4d4d4',
        padding: '2em 1em',
        display: 'flex',
        flexDirection: 'row',
        paddingRight: '36px',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    verifyText: {
        maxWidth: '30em'
    },
    verifyTitle: {
        fontWeight: '700!important'
    },
    startKYX: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 1em!important'
    },
    doItLater: {
        marginTop: '10em'
    },
    underText: {
        color: '#617380',
        fontSize: '11px'
    },
    scrollEnd: {
        padding: '0',
        fontSize: '12px',
        color: '#1BAAE2',
        marginBottom: '5px',
        '&:hover': {
            backgroundColor: 'transparent'
        }
    }
});

export const KYXEulaFirstStep = ({ handleChange }) => {
    const classes = useStyles();

    const { userInfo } = useContext(AuthContext);

    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    const handleAccept = async () => {
        if (isChecked) {
            await Users.updateLastEULA(userInfo.id, EULAS.E_SIGNATURE_AGREEMENT);
            handleChange(6);
        }
    };

    useEffect(() => {
        if (isScrolledToBottom) {
            setIsCheckboxEnabled(true);
        }
    }, [isScrolledToBottom]);

    const handleScroll = event => {
        const element = event.target;
        const threshold = 20;
        if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
            setIsScrolledToBottom(true);
        } else {
            setIsScrolledToBottom(false);
        }
    };

    const handleScrollToBottom = () => {
        const element = document.getElementById('terms-and-conditions');
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    };

    return (
        <Box className={classes.dialogBody}>
            <Box
                className={classes.banner}
                sx={{
                    display: { lg: 'initial', md: 'initial', sm: 'none', xs: 'none' }
                }}>
                <CardDouble />
                <Box className={classes.bannerText}>
                    <Typography variant="h5" component="h5" className={classes.bannerTitle}>
                        Welcome to the Paystand DeFi Corporate Card!
                    </Typography>
                    <Typography className={classes.bannerSpan}>
                        Manage all of your expenses with virtual cards that work exactly like credit cards, and give you reward
                        points in bitcoin.
                    </Typography>
                </Box>
            </Box>
            <Box className={classes.content}>
                <Typography variant="h6" className={classes.contentTitle}>
                    E-Signature Agreement
                </Typography>
                <Box>
                    <Box
                        id="terms-and-conditions"
                        onScroll={handleScroll}
                        style={{
                            maxHeight: '330px',
                            overflowY: 'scroll',
                            marginTop: '15px',
                            fontSize: '13px',
                            padding: '15px',
                            fontWeight: '500',
                            color: '#182D55',
                            border: '1px solid #DFE3E6',
                            borderRadius: '5px'
                        }}>
                        <h4>E-Signature Agreement</h4>
                        <p>
                            This Electronic Communications Disclosure and Agreement (this "Agreement") applies to all electronic
                            communications for the deposit accounts established for you by Lewis & Clark Bank, Member FDIC (each,
                            an “Account," and collectively, the “Accounts”) using the services provided by Paystand, Inc d/b/a
                            Paystand (“Paystand”) via <u>https://www.paystand.com/corporate-card</u> (the "Website") or the
                            Paystand mobile application ("Mobile App"). "We", "us", "our", refers to Paystand and/or Lewis & Clark
                            Bank, Member FDIC. "You" and "your" mean the person identified on the Account. "Communication” or
                            “communications” means any message, correspondence, document, writing, disclosure, or other
                            information we may provide to you electronically for or relating to the Accounts. We designed the
                            Accounts for natural persons who choose to communicate with us electronically and who are able to
                            receive notices and communications exclusively through the Website, the Mobile App, or by electronic
                            mail ("e-mail").
                        </p>
                        <br />
                        <p>
                            1. <u>Scope of Communications to Be Provided in Electronic Form.</u> When you apply for an Account,
                            you agree that any communications will be provided in electronic format, to the extent allowed by law,
                            and we will not send paper communications. Your consent to receive electronic communications and
                            transactions includes:
                        </p>

                        <ol type="a">
                            <li>
                                All legal and regulatory disclosures and communications associated with the Accounts and any
                                related products or services;
                            </li>
                            <li>The legal agreements for all Accounts and any notices about changes in terms;</li>
                            <li>Privacy policies and notices;</li>
                            <li>Responses to claims filed with the Accounts;</li>
                            <li>Notices regarding overdue payments and collection notices; and</li>
                            <li>
                                All other communications from us for the Accounts and any related transactions, products, or
                                services.
                            </li>
                        </ol>

                        <p>
                            2. <u>Method of Providing Communications in Electronic Form.</u> We will provide all communications in
                            electronic form either: (a) by email; (b) through the Website, or (c) through the Mobile App.
                        </p>

                        <p>
                            3. <u>How to Withdraw Consent.</u> You may withdraw your consent to receive communications in
                            electronic form by emailing <u>support@paystand.com</u>. If you withdraw your consent, we will send
                            you a notice and advise you of the last date you will be able to make deposits, withdrawals,
                            transfers, and payments to and from your Account. The legal validity and enforceability of our prior
                            electronic communications are not affected if you withdraw consent to electronic delivery of
                            communications.
                        </p>

                        <p>
                            4. <u>Consequences of Withdrawing Consent.</u> If you do not agree to receive all communications from
                            us in electronic form, you may not open an Account with us. If you withdraw your consent after
                            providing consent to delivery and receipt of electronic communications, we will close your Account and
                            terminate your relationship with us.
                        </p>

                        <p>
                            5. <u>How to Update Records.</u> You will need to provide us with a valid, accurate, and complete
                            email address, mobile and residential telephone numbers (if you have a residential telephone), contact
                            information, and other information for electronic communications. You agree to promptly update any
                            changes in this information using the Website or Mobile App. Your timely and complete notification to
                            us of your latest contact information will allow us to send communications to you, otherwise we will
                            send communications to the last known e-mail address you provided to us.
                        </p>

                        <p>
                            6. <u>Hardware and Software Requirements.</u> In order to access and retain electronic communications,
                            you will need the following computer hardware and software: (a) a computer with an Internet
                            connection; (b) current “web browser” that includes 128-bit encryption with cookies enabled; (c) a
                            current version of Adobe Acrobat Reader to open documents in .pdf format; (d) a valid email address
                            (the primary email address designated by you); and (e) sufficient storage space to save past
                            communications or an installed printer to print them. We will update these requirements as necessary
                            to preserve the ability to receive electronic communications and notify you if there is a substantial
                            change in these requirements.
                        </p>

                        <p>
                            7. <u>Requesting Paper Copies.</u> We will not send paper copies of any communication. You may obtain
                            a paper copy of any electronic communication by printing it from the Website or Mobile App.
                        </p>

                        <p>
                            8. <u>Electronic Communications Are Writings.</u> All communications in electronic form from us to you
                            are considered "in writing." You may want to print or download a copy of this disclosure and any other
                            electronic communications. We will maintain electronic copies of all records related to your Accounts
                            according to our record retention policy.
                        </p>

                        <p>
                            9. <u>Federal Law.</u> You acknowledge that consent to electronic communications is being provided for
                            a transaction affecting interstate commerce that is subject to the federal Electronic Signatures in
                            Global and National Commerce Act (the "E-SIGN Act") and that you and we both intend that the E-SIGN
                            Act applies to all transactions between us.
                        </p>

                        <p>
                            10. <u>Termination/Changes.</u> We may discontinue the provision of electronic communications or
                            terminate or change the terms and conditions upon which electronic communications are provided, but we
                            will provide you with notice of any such termination or change.
                        </p>
                    </Box>
                    <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button className={classes.scrollEnd} onClick={handleScrollToBottom} style={{ textTransform: 'none' }}>
                            scroll to the end
                        </Button>
                    </Box>
                    <Typography className={classes.underText}>
                        By agreeing to the E-Signature Agreement, you consent to receive electronic disclosures and/or statements
                        for your Paystand account and consent to be legally bound to this agreement.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isChecked}
                                disabled={!isCheckboxEnabled}
                                onChange={() => setIsChecked(!isChecked)}
                            />
                        }
                        label="I accept the E-Signature Agreement"
                    />
                    <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="primary" onClick={handleAccept} disabled={!isChecked}>
                            Next
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
